import { SyntheticEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupIcon from "@mui/icons-material/Group";
import { OptifundDashboardTab } from "../../../../../../features/optifundDashboardSlice";
import Performance from "./OptifundDashboardMobileModules/Performance/Performance";
import Brands from "./OptifundDashboardMobileModules/Brands/Brands";
import Users from "./OptifundDashboardMobileModules/Users/Users";

interface OptifundDashboardMobileTabsProps {
  userToken: any;
  websiteSharedUid: string;
}

function isOptifundDashboardTab(
  value: string | null,
): value is OptifundDashboardTab {
  return value === "performance" || value === "brands" || value === "users";
}

export default function OptifundDashboardMobileTabs({
  userToken,
  websiteSharedUid,
}: OptifundDashboardMobileTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<OptifundDashboardTab>(() => {
    const tab = searchParams.get("tab");
    return isOptifundDashboardTab(tab) ? tab : "performance";
  });
  // Each tab lazy-mounts on first visit and then stays mounted (hidden via
  // display:none rather than unmounted) so switching back to it doesn't
  // re-trigger its fetch - mirrors the desktop OptifundDashboardTabs.tsx.
  const [visitedTabs, setVisitedTabs] = useState<OptifundDashboardTab[]>(() =>
    selectedTab === "performance"
      ? ["performance"]
      : ["performance", selectedTab],
  );

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedTab === "performance") next.delete("tab");
        else next.set("tab", selectedTab);
        return next;
      },
      { replace: true },
    );
  }, [selectedTab, setSearchParams]);

  const handleChange = (
    _event: SyntheticEvent,
    newTab: OptifundDashboardTab,
  ) => {
    setSelectedTab(newTab);
    setVisitedTabs((current) =>
      current.includes(newTab) ? current : [...current, newTab],
    );
  };

  return (
    // The bottom nav is `position: "fixed"`, pinned to the actual device
    // viewport rather than to any scroll container this component owns.
    // An earlier attempt used `position: "sticky", bottom: 0` inside a
    // `height: "93vh"` + `overflowY: "auto"` box (this codebase's own
    // convention for pinned bars elsewhere, e.g. DashboardMobileTabs.tsx's
    // sticky top AppBar - just flipped to the bottom edge) - that only
    // keeps an element pinned within ITS OWN scroll container. This
    // component sits inside WorkspaceLayout's page, and on a real device
    // (confirmed on a Samsung S24) the outer page can scroll too - as the
    // mobile browser's address bar hides/shows, the whole 93vh box (nav
    // included) can drift within the page, so the "sticky" nav intermittently
    // scrolled out of view instead of staying pinned. `position: "fixed"`
    // pins it to the viewport unconditionally, independent of any
    // page-level or component-level scrolling - the content below gets
    // matching bottom padding so the fixed bar never covers it.
    <>
      <Box sx={{ p: 2, mt: 1, pb: 9 }}>
        {/* Persistent header shown above whichever tab is active - matches
            the mockup's <h1>/<p> pair at the top of its scroll area. */}
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 700,
            color: "#1a1a1a",
            letterSpacing: "-0.2px",
          }}
        >
          Optifund Dashboard
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            color: "#757575",
            mt: 0.5,
            mb: 2,
            lineHeight: 1.45,
          }}
        >
          Acquisition performance, brand payouts, and the applicant funnel.
        </Typography>

        <Box sx={{ display: selectedTab === "performance" ? "block" : "none" }}>
          {visitedTabs.includes("performance") && (
            <Performance
              userToken={userToken}
              websiteSharedUid={websiteSharedUid}
            />
          )}
        </Box>
        <Box sx={{ display: selectedTab === "brands" ? "block" : "none" }}>
          {visitedTabs.includes("brands") && (
            <Brands userToken={userToken} websiteSharedUid={websiteSharedUid} />
          )}
        </Box>
        <Box sx={{ display: selectedTab === "users" ? "block" : "none" }}>
          {visitedTabs.includes("users") && (
            <Users userToken={userToken} websiteSharedUid={websiteSharedUid} />
          )}
        </Box>
      </Box>

      <BottomNavigation
        value={selectedTab}
        onChange={handleChange}
        showLabels
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: "1px solid #ececec",
          zIndex: 5,
          // BottomNavigationAction has no `color` prop (unlike Tabs/Button),
          // so its selected state falls back to the theme's primary (MUI
          // default blue) unless overridden here - this product never shows
          // that blue (checklist Section 0). #8E24AA is the same brand hex
          // used throughout this module's charts/icons wherever no color
          // prop exists to set to "secondary" instead.
          // The icon needs its OWN selector (.MuiSvgIcon-root), not just the
          // label - MUI's built-in `.Mui-selected` rule for the icon is more
          // specific than plain color inheritance from the parent, so
          // overriding only the root/label (as a first attempt did) left
          // the icon itself still rendering primary blue.
          "& .Mui-selected, & .Mui-selected .MuiBottomNavigationAction-label, & .Mui-selected .MuiSvgIcon-root": {
            color: "#8E24AA",
          },
        }}
      >
        <BottomNavigationAction
          label="Performance"
          value="performance"
          icon={<TrendingUpIcon />}
        />
        <BottomNavigationAction
          label="Brands"
          value="brands"
          icon={<StorefrontIcon />}
        />
        <BottomNavigationAction
          label="Users"
          value="users"
          icon={<GroupIcon />}
        />
      </BottomNavigation>
    </>
  );
}
